var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useRef, useState, useCallback } from "react";
import { getUploadURL } from "../ContentAPI";
import { uploadImageDataDirect } from "../util/ImageUtils";
import { uploadVideo } from "../util/VideoUtils";
import { useProfileAuthStore } from "../store/ProfileStore";
import { useLogger } from "../logger";
const LOGPREFIX = "useFileUploader: ";
const getFileExtension = (file) => {
    const name = file.name.toLowerCase();
    if (file.type === "image/png" || name.endsWith(".png"))
        return "png";
    if (file.type === "image/jpeg" ||
        name.endsWith(".jpg") ||
        name.endsWith(".jpeg"))
        return "jpg";
    if (name.endsWith(".mp4"))
        return "mp4";
    if (name.endsWith(".mov"))
        return "mov";
    return "jpg";
};
const isVideoExtension = (ext) => ext === "mp4" || ext === "mov";
// Read fresh auth state from the Zustand store rather than from the
// captured `auth` snapshot. The snapshot is frozen at render time, so a
// previous serial iteration's setBearerAndSequence wouldn't be visible
// otherwise — and we'd hand the server the same stale nonce on every call.
const requestUploadURL = (contentId, partition, fileExtension, cacheBusterStore, logger) => {
    return new Promise((resolve, reject) => {
        const auth = useProfileAuthStore.getState();
        if (!auth.isSignedIn() || !auth.bearer || !auth.sequence || !auth.userId) {
            reject(new Error("User is not authenticated"));
            return;
        }
        getUploadURL((ok, status, response) => {
            if (!ok || !(response === null || response === void 0 ? void 0 : response.url)) {
                let errorMessage = "Failed to get upload URL";
                if (response === null || response === void 0 ? void 0 : response.error) {
                    errorMessage = response.error;
                }
                else if (status === 401) {
                    errorMessage = "Authentication failed. Please sign in again.";
                }
                else if (status === 403) {
                    errorMessage = "Access denied. Please check your permissions.";
                }
                else if (status >= 500) {
                    errorMessage = "Server error. Please try again later.";
                }
                logger.error(LOGPREFIX +
                    "requestUploadURL: " +
                    errorMessage +
                    " (status: " +
                    status +
                    ")");
                reject(new Error(errorMessage));
                return;
            }
            if (response.bearer && response.sequence) {
                // Use the current store reference for the setter too, so we never
                // dispatch through a stale closure.
                useProfileAuthStore
                    .getState()
                    .setBearerAndSequence(response.bearer, response.sequence, logger);
            }
            resolve(response.url);
        }, auth.userId, contentId, partition, fileExtension, auth.bearer, auth.sequence, cacheBusterStore);
    });
};
const putImageToS3 = (uploadUrl, file, cacheBusterStore, logger) => new Promise((resolve, reject) => {
    uploadImageDataDirect(uploadUrl, file, () => resolve(), (_clientDetailCode, errorMessage) => reject(new Error(errorMessage)), cacheBusterStore, logger);
});
// Videos use uploadVideo, which itself fetches a presigned URL then PUTs.
// We pre-fetch the URL ourselves in the batch path, so for parity we still
// call uploadVideo but it will re-fetch (cheap) and rotate the bearer too.
// To avoid that double fetch we PUT directly using the URL we already have.
const putVideoToS3 = (uploadUrl, file) => new Promise((resolve, reject) => {
    const headers = new Headers();
    headers.append("Content-Type", "application/octet-stream");
    fetch(new Request(uploadUrl, { method: "PUT", body: file, headers }))
        .then((res) => {
        if (res.ok)
            resolve();
        else
            reject(new Error(`Upload failed with status ${res.status}: ${res.statusText}`));
    })
        .catch((err) => reject(err instanceof Error ? err : new Error(String(err))));
});
export const useFileUploader = (options = {}) => {
    const logger = useLogger((state) => state);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const fileInputRef = useRef(null);
    const { maxFileSize = 1024 * 1024 * 1024, // 1GB default (for backward compatibility)
    maxImageSize = 50 * 1024 * 1024, // 50MB for images
    maxVideoSize = 1024 * 1024 * 1024, // 1GB for videos
    acceptedFileTypes = [
        "image/jpeg",
        "image/png",
        "video/mp4",
        "video/quicktime",
    ], onUploadStart, onUploadProgress, onUploadComplete, onUploadError, } = options;
    const validateFile = useCallback((file) => {
        const isVideo = file.type === "video/mp4" || file.type === "video/quicktime";
        const isImage = file.type === "image/jpeg" || file.type === "image/png";
        let maxAllowedSize = maxFileSize;
        let fileTypeLabel = "file";
        if (isVideo) {
            maxAllowedSize = maxVideoSize;
            fileTypeLabel = "video";
        }
        else if (isImage) {
            maxAllowedSize = maxImageSize;
            fileTypeLabel = "image";
        }
        if (file.size > maxAllowedSize) {
            const sizeLimitMB = maxAllowedSize >= 1024 * 1024 * 1024
                ? `${maxAllowedSize / (1024 * 1024 * 1024)}GB`
                : `${maxAllowedSize / (1024 * 1024)}MB`;
            return new Error(`${fileTypeLabel.charAt(0).toUpperCase() + fileTypeLabel.slice(1)} size exceeds maximum allowed size of ${sizeLimitMB}`);
        }
        if (!acceptedFileTypes.includes(file.type)) {
            return new Error(`File type ${file.type} is not supported`);
        }
        return null;
    }, [acceptedFileTypes, maxFileSize, maxImageSize, maxVideoSize]);
    const uploadFile = useCallback((params) => {
        const { file, contentId, partition, auth, cacheBusterStore } = params;
        logger.log(LOGPREFIX + "uploadFile: starting upload for " + file.name);
        setIsUploading(true);
        setError(null);
        setUploadProgress(0);
        onUploadStart === null || onUploadStart === void 0 ? void 0 : onUploadStart();
        return new Promise((resolve, reject) => {
            const validationError = validateFile(file);
            if (validationError) {
                setError(validationError);
                setIsUploading(false);
                onUploadError === null || onUploadError === void 0 ? void 0 : onUploadError(validationError);
                reject(validationError);
                return;
            }
            const fileExtension = getFileExtension(file);
            const isVideo = isVideoExtension(fileExtension);
            const isImage = !isVideo;
            logger.log(LOGPREFIX + "uploadFile: fileExtension = " + fileExtension);
            logger.log(LOGPREFIX + "uploadFile: contentId = " + contentId);
            if (!auth.isSignedIn() ||
                !auth.bearer ||
                !auth.sequence ||
                !auth.userId) {
                const err = new Error("User is not authenticated");
                setError(err);
                setIsUploading(false);
                onUploadError === null || onUploadError === void 0 ? void 0 : onUploadError(err);
                reject(err);
                return;
            }
            // Get presigned URL using existing API
            getUploadURL((ok, status, response) => {
                if (!ok || !(response === null || response === void 0 ? void 0 : response.url)) {
                    let errorMessage = "Failed to get upload URL";
                    if (response === null || response === void 0 ? void 0 : response.error) {
                        errorMessage = response.error;
                    }
                    else if (status === 401) {
                        errorMessage = "Authentication failed. Please sign in again.";
                    }
                    else if (status === 403) {
                        errorMessage = "Access denied. Please check your permissions.";
                    }
                    else if (status >= 500) {
                        errorMessage = "Server error. Please try again later.";
                    }
                    logger.error(LOGPREFIX +
                        "getUploadURL failed: " +
                        errorMessage +
                        " (status: " +
                        status +
                        ")");
                    const err = new Error(errorMessage);
                    setError(err);
                    setIsUploading(false);
                    onUploadError === null || onUploadError === void 0 ? void 0 : onUploadError(err);
                    reject(err);
                    return;
                }
                if (response.bearer && response.sequence) {
                    auth.setBearerAndSequence(response.bearer, response.sequence, logger);
                }
                const uploadUrl = response.url;
                logger.log(LOGPREFIX + "uploadFile: got upload URL");
                if (isImage) {
                    uploadImageDataDirect(uploadUrl, file, (clientDetailCode) => {
                        logger.log(LOGPREFIX +
                            "uploadFile: image upload complete - " +
                            clientDetailCode);
                        setUploadProgress(100);
                        onUploadProgress === null || onUploadProgress === void 0 ? void 0 : onUploadProgress(100);
                        setIsUploading(false);
                        const result = { success: true, contentId };
                        onUploadComplete === null || onUploadComplete === void 0 ? void 0 : onUploadComplete(result);
                        resolve(result);
                    }, (clientDetailCode, errorMessage) => {
                        logger.error(LOGPREFIX +
                            "uploadFile: image upload failed - " +
                            errorMessage);
                        const err = new Error(errorMessage);
                        setError(err);
                        setIsUploading(false);
                        onUploadError === null || onUploadError === void 0 ? void 0 : onUploadError(err);
                        reject(err);
                    }, cacheBusterStore, logger);
                }
                else {
                    logger.log(LOGPREFIX + "uploadFile: uploading video");
                    uploadVideo({
                        videoFile: file,
                        targetParameters: {
                            contentId,
                            fileType: fileExtension,
                            partition,
                        },
                    }, {
                        okCallback: (clientDetailCode) => {
                            logger.log(LOGPREFIX +
                                "uploadFile: video upload complete - " +
                                clientDetailCode);
                            setUploadProgress(100);
                            onUploadProgress === null || onUploadProgress === void 0 ? void 0 : onUploadProgress(100);
                            setIsUploading(false);
                            const result = { success: true, contentId };
                            onUploadComplete === null || onUploadComplete === void 0 ? void 0 : onUploadComplete(result);
                            resolve(result);
                        },
                        errorCallback: (clientDetailCode, errorMessage) => {
                            logger.error(LOGPREFIX +
                                "uploadFile: video upload failed - " +
                                errorMessage);
                            const err = new Error(errorMessage);
                            setError(err);
                            setIsUploading(false);
                            onUploadError === null || onUploadError === void 0 ? void 0 : onUploadError(err);
                            reject(err);
                        },
                        authCallback: {
                            isSignedIn: () => auth.isSignedIn(),
                            bearer: () => auth.bearer,
                            sequence: () => auth.sequence,
                            setBearerAndSequence: (bearer, sequence) => {
                                auth.setBearerAndSequence(bearer, sequence, logger);
                            },
                            userId: () => auth.userId,
                        },
                    }, cacheBusterStore);
                }
            }, auth.userId, contentId, partition, fileExtension, auth.bearer, auth.sequence, cacheBusterStore);
        }).catch((err) => {
            const e = err;
            logger.error(LOGPREFIX + "uploadFile: error - " + e.message);
            setError(e);
            setIsUploading(false);
            onUploadError === null || onUploadError === void 0 ? void 0 : onUploadError(e);
            return { success: false, contentId, error: e.message };
        });
    }, [
        logger,
        validateFile,
        onUploadStart,
        onUploadProgress,
        onUploadComplete,
        onUploadError,
    ]);
    const uploadFiles = useCallback((params) => __awaiter(void 0, void 0, void 0, function* () {
        const { files, partition, cacheBusterStore, onFileComplete } = params;
        logger.log(LOGPREFIX + "uploadFiles: batch of " + files.length + " files");
        setIsUploading(true);
        setError(null);
        setUploadProgress(0);
        onUploadStart === null || onUploadStart === void 0 ? void 0 : onUploadStart();
        // Validate every file up front so we fail fast.
        for (const input of files) {
            const err = validateFile(input.file);
            if (err) {
                setError(err);
                setIsUploading(false);
                onUploadError === null || onUploadError === void 0 ? void 0 : onUploadError(err);
                throw err;
            }
        }
        // Phase 1: SERIAL getUploadURL calls.
        // Each successful response rotates auth.bearer/auth.sequence, and the
        // next iteration reads the fresh values. Running these in parallel is
        // what breaks the bearer.checkNonce replay check on the server.
        const uploadTargets = [];
        for (let i = 0; i < files.length; i++) {
            const input = files[i];
            const ext = getFileExtension(input.file);
            try {
                const url = yield requestUploadURL(input.contentId, partition, ext, cacheBusterStore, logger);
                uploadTargets.push({ input, ext, url });
            }
            catch (err) {
                const e = err;
                const result = {
                    file: input.file,
                    contentId: input.contentId,
                    success: false,
                    error: e.message,
                };
                onFileComplete === null || onFileComplete === void 0 ? void 0 : onFileComplete(i, result);
                // Mark remaining files as failed so the UI surfaces them too.
                const failures = files.map((f, j) => {
                    if (j < i) {
                        return {
                            file: f.file,
                            contentId: f.contentId,
                            success: true,
                        };
                    }
                    if (j === i)
                        return result;
                    const r = {
                        file: f.file,
                        contentId: f.contentId,
                        success: false,
                        error: "Skipped: upload URL fetch aborted after earlier failure",
                    };
                    onFileComplete === null || onFileComplete === void 0 ? void 0 : onFileComplete(j, r);
                    return r;
                });
                setIsUploading(false);
                setError(e);
                onUploadError === null || onUploadError === void 0 ? void 0 : onUploadError(e);
                return failures;
            }
        }
        // Phase 2: PARALLEL S3 PUTs. Presigned URLs do not require the bearer,
        // so this is safe to fan out — the bandwidth bottleneck is the user's
        // upstream, not the API.
        const results = yield Promise.all(uploadTargets.map((target, index) => __awaiter(void 0, void 0, void 0, function* () {
            try {
                if (isVideoExtension(target.ext)) {
                    yield putVideoToS3(target.url, target.input.file);
                }
                else {
                    yield putImageToS3(target.url, target.input.file, cacheBusterStore, logger);
                }
                const r = {
                    file: target.input.file,
                    contentId: target.input.contentId,
                    success: true,
                };
                onFileComplete === null || onFileComplete === void 0 ? void 0 : onFileComplete(index, r);
                return r;
            }
            catch (err) {
                const e = err;
                const r = {
                    file: target.input.file,
                    contentId: target.input.contentId,
                    success: false,
                    error: e.message,
                };
                onFileComplete === null || onFileComplete === void 0 ? void 0 : onFileComplete(index, r);
                return r;
            }
        })));
        const allSucceeded = results.every((r) => r.success);
        setUploadProgress(allSucceeded ? 100 : 0);
        onUploadProgress === null || onUploadProgress === void 0 ? void 0 : onUploadProgress(allSucceeded ? 100 : 0);
        setIsUploading(false);
        if (!allSucceeded) {
            const firstFailure = results.find((r) => !r.success);
            const err = new Error((firstFailure === null || firstFailure === void 0 ? void 0 : firstFailure.error) || "One or more files failed to upload");
            setError(err);
            onUploadError === null || onUploadError === void 0 ? void 0 : onUploadError(err);
        }
        return results;
    }), [logger, validateFile, onUploadStart, onUploadProgress, onUploadError]);
    const selectFile = useCallback(() => {
        var _a;
        (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    }, []);
    const resetUpload = useCallback(() => {
        logger.log(LOGPREFIX + "resetUpload");
        setIsUploading(false);
        setUploadProgress(0);
        setError(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
    }, [previewUrl, logger]);
    return {
        isUploading,
        uploadProgress,
        error,
        previewUrl,
        uploadFile,
        uploadFiles,
        resetUpload,
        selectFile,
        fileInputRef,
    };
};
